// src/share/CustomerPicker.jsx
//
// Reusable customer-selection block for email share modals. Extracted from
// CreationEmailModalInline (sharingCenter.jsx) so the same picker can be
// dropped into the Profile / Company / Team share modals — Brett wants the
// "Send to customers" picker available everywhere a user emails a share, not
// just on creation posts.
//
// Self-contained: fetches its own customer list on mount, manages its own
// search query state and dropdown visibility. Parent owns the
// selectedCustomers array (and gets onChange callbacks) so the parent can
// roll those emails into its own send payload.

import React, { useEffect, useState, useRef } from 'react';
import { useProfileStore } from '../state/profileStore';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * @param {Array}    selectedCustomers  array of { customer_id, name, email }
 * @param {Function} onChange           called with the new array on add/remove
 * @param {string}   [label='Send to customers:']
 */
export default function CustomerPicker({ selectedCustomers = [], onChange, label = 'Send to customers:' }) {
  const profile = useProfileStore((s) => s.profile);

  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch customer list on mount, filter to those with email addresses
  useEffect(() => {
    if (!profile?.u_id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/profile/customers?u_id=${profile.u_id}`, { credentials: 'include' });
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) {
          setCustomers(data.filter((c) => c.customer_email));
        }
      } catch (err) {
        console.error('[CustomerPicker] failed to load customers:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [profile?.u_id]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedIds = new Set(selectedCustomers.map((c) => c.customer_id));
  const filtered = customers.filter((c) => {
    if (selectedIds.has(c.customer_id)) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    const name = `${c.customer_firstname || ''} ${c.customer_lastname || ''}`.toLowerCase();
    return name.includes(q) || (c.customer_email || '').toLowerCase().includes(q);
  });

  const addCustomer = (c) => {
    onChange?.([
      ...selectedCustomers,
      {
        customer_id: c.customer_id,
        name: [c.customer_firstname, c.customer_lastname].filter(Boolean).join(' '),
        email: c.customer_email,
      },
    ]);
    setQuery('');
    setShowDropdown(false);
  };

  const removeCustomer = (id) => {
    onChange?.(selectedCustomers.filter((c) => c.customer_id !== id));
  };

  return (
    <div ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>

      {/* Selected customer pills */}
      {selectedCustomers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedCustomers.map((c) => (
            <span
              key={c.customer_id}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-[#00aef7]/10 text-[#00aef7] border border-[#00aef7]/20 rounded-full"
            >
              {c.name}
              <button
                onClick={() => removeCustomer(c.customer_id)}
                className="ml-0.5 text-[#00aef7]/60 hover:text-[#00aef7] text-sm leading-none"
                title="Remove"
                type="button"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          placeholder={customers.length ? 'Search customers by name or email…' : 'No customers with email addresses'}
          disabled={!customers.length}
          autoComplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          className="w-full p-2 border rounded focus:ring-2 focus:ring-[#00aef7] focus:outline-none text-sm"
        />

        {/* Dropdown */}
        {showDropdown && filtered.length > 0 && (
          <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
            {filtered.slice(0, 20).map((c) => {
              const name = [c.customer_firstname, c.customer_lastname].filter(Boolean).join(' ');
              return (
                <button
                  key={c.customer_id}
                  onClick={() => addCustomer(c)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#00aef7]/5 transition-colors flex items-center justify-between"
                  type="button"
                >
                  <span className="font-medium text-gray-800">{name || 'Unnamed'}</span>
                  <span className="text-xs text-gray-400 ml-2 truncate">{c.customer_email}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
